import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, ShieldCheck, Trash2, RefreshCw, History, Settings2, Mail } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

// iter 277: CI reservation verification — Admin / CI admin only.
const Th = ({ children, right, w }) => <th className={`text-[10px] uppercase tracking-label text-gray-500 font-semibold px-3 py-2 ${right ? "text-right" : "text-left"} ${w || ""}`}>{children}</th>;
const Td = ({ children, cls = "" }) => <td className={`px-3 py-2 text-xs text-gray-800 ${cls}`}>{children}</td>;

function LogsDialog({ open, onClose, t }) {
  const [logs, setLogs] = useState({ items: [], total: 0 });
  useEffect(() => { if (open) api.get("/ci-reservations/logs", { params: { page_size: 300 } }).then((r) => setLogs(r.data)); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="ci-logs-dialog">
        <DialogHeader><DialogTitle className="text-base">{t("ci.logs")} · {logs.total}</DialogTitle></DialogHeader>
        <div className="border border-gray-200 overflow-auto max-h-[60vh]">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0"><tr><Th>{t("samples.time")}</Th><Th>{t("ci.action")}</Th><Th>{t("samples.product")}</Th><Th>{t("samples.quotation")}</Th><Th right>{t("ci.qty")}</Th><Th>{t("samples.note")}</Th><Th>{t("samples.user")}</Th></tr></thead>
            <tbody>
              {logs.items.map((l) => (
                <tr key={l.id} className="border-t border-gray-100" data-testid={`ci-log-row-${l.id}`}>
                  <Td cls="font-mono text-[11px] text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</Td>
                  <Td>{t(`ci.action_${l.action}`, l.action)}</Td>
                  <Td><span className="font-mono">{l.item_no}</span> <span className="text-gray-400">{l.size}</span></Td>
                  <Td>{l.cart_name}</Td>
                  <Td cls="text-right font-mono">{l.qty}</Td>
                  <Td>{l.note}</Td>
                  <Td>{l.user_name}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CiReservationsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], total: 0, stale_days: 30, stale_count: 0, users: [], carts: [] });
  const [filter, setFilter] = useState({ q: "", cart_id: "", created_by: "", stale_only: false, status: "active" });
  const [selected, setSelected] = useState(new Set());
  const [logsOpen, setLogsOpen] = useState(false);
  const [staleInput, setStaleInput] = useState("");

  const load = useCallback(async () => {
    const params = { status: filter.status, q: filter.q || undefined, cart_id: filter.cart_id || undefined, created_by: filter.created_by || undefined, stale_only: filter.stale_only || undefined, page_size: 500 };
    const { data } = await api.get("/ci-reservations", { params });
    setData(data); setSelected(new Set()); setStaleInput(String(data.stale_days));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); }
  };
  const keep = (r) => run(() => api.post(`/ci-reservations/${r.id}/keep`), t("ci.kept"));
  const release = (r) => { const reason = window.prompt(t("ci.release_reason_prompt"), ""); if (reason === null) return; run(() => api.post(`/ci-reservations/${r.id}/release`, { reason: reason || undefined }), t("ci.released")); };
  const bulk = (action) => {
    const ids = [...selected]; if (!ids.length) return;
    if (action === "release" && !window.confirm(t("ci.bulk_release_confirm", { count: ids.length }))) return;
    run(() => api.post(`/ci-reservations/bulk-${action}`, { ids }), action === "release" ? t("ci.released") : t("ci.kept"));
  };
  const sendDigest = async () => {
    try {
      const { data } = await api.post("/ci-reservations/send-digest");
      if (data.sent) toast.success(t("ci.digest_sent", { count: data.sent_to?.length ?? data.recipients.length, stale: data.stale }) + (data.error ? ` (${data.error})` : ""));
      else toast.error(data.error || t("samples.failed"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); }
  };
  const saveStale = () => run(() => api.put("/ci-reservations/settings", { stale_days: Number(staleInput) || 30 }), t("ci.settings_saved"));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allIds = data.items.map((r) => r.id);

  return (
    <AppLayout>
      <div className="space-y-6 max-w-7xl" data-testid="ci-reservations-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">{t("ci.title")}</h1>
            <p className="text-sm text-gray-500 mt-1">{t("ci.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-600" data-testid="ci-stale-days-block">
              <Settings2 size={13} />
              <span>{t("ci.stale_days")}</span>
              <Input type="number" min="1" max="365" value={staleInput} onChange={(e) => setStaleInput(e.target.value)} className="h-8 w-16 text-xs text-center" data-testid="ci-stale-days-input" />
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={saveStale} data-testid="ci-stale-days-save">{t("mobile.save")}</Button>
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={sendDigest} data-testid="ci-send-digest"><Mail size={13} className="mr-1" />{t("ci.send_digest")}</Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setLogsOpen(true)} data-testid="ci-logs-open"><History size={13} className="mr-1" />{t("ci.logs")}</Button>
            <Button variant="ghost" size="sm" className="h-8" onClick={load} data-testid="ci-refresh"><RefreshCw size={14} /></Button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="ci-filters">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
            <Input value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} placeholder={t("ci.search_placeholder")} className="h-8 pl-7 w-56 text-xs" data-testid="ci-filter-q" />
          </div>
          <select value={filter.cart_id} onChange={(e) => setFilter((f) => ({ ...f, cart_id: e.target.value }))} className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white" data-testid="ci-filter-cart">
            <option value="">{t("ci.all_quotations")}</option>
            {data.carts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filter.created_by} onChange={(e) => setFilter((f) => ({ ...f, created_by: e.target.value }))} className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white" data-testid="ci-filter-user">
            <option value="">{t("ci.all_users")}</option>
            {data.users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white" data-testid="ci-filter-status">
            <option value="active">{t("ci.status_active")}</option>
            <option value="released">{t("ci.status_released")}</option>
          </select>
          <button type="button" onClick={() => setFilter((f) => ({ ...f, stale_only: !f.stale_only }))} data-testid="ci-filter-stale"
            className={`h-8 px-3 text-xs rounded-md border ${filter.stale_only ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-white border-gray-300 text-gray-600"}`}>
            {t("ci.stale_only")} ({data.stale_count})
          </button>
          {selected.size > 0 && (
            <div className="ml-auto flex items-center gap-2" data-testid="ci-bulk-bar">
              <span className="text-xs text-gray-500">{t("ci.selected", { count: selected.size })}</span>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => bulk("keep")} data-testid="ci-bulk-keep"><ShieldCheck size={13} className="mr-1" />{t("ci.keep")}</Button>
              <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => bulk("release")} data-testid="ci-bulk-release"><Trash2 size={13} className="mr-1" />{t("ci.release")}</Button>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 overflow-auto">
          <table className="w-full" data-testid="ci-table">
            <thead className="bg-gray-50">
              <tr>
                <Th w="w-8"><input type="checkbox" checked={allIds.length > 0 && selected.size === allIds.length} onChange={(e) => setSelected(e.target.checked ? new Set(allIds) : new Set())} data-testid="ci-select-all" /></Th>
                <Th>{t("samples.product")}</Th><Th>{t("samples.quotation")}</Th><Th right>{t("ci.qty")}</Th><Th>{t("ci.reserved_by")}</Th>
                <Th>{t("ci.reserved_at")}</Th><Th>{t("ci.verified_at")}</Th><Th right>{t("ci.age")}</Th><Th>{t("samples.note")}</Th><Th />
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={10} className="text-center text-xs text-gray-400 py-10">{t("samples.empty")}</td></tr>}
              {data.items.map((r) => (
                <tr key={r.id} className={`border-t border-gray-100 ${r.stale ? "bg-amber-50" : ""}`} data-testid={`ci-row-${r.id}`}>
                  <Td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`ci-select-${r.id}`} /></Td>
                  <Td><span className="font-mono">{r.item_no}</span> <span className="text-gray-400">{r.size}</span><p className="text-[10px] text-gray-400 truncate max-w-[240px]">{r.description}</p></Td>
                  <Td>{r.cart_name}</Td>
                  <Td cls="text-right font-mono font-semibold">{r.qty}</Td>
                  <Td>{r.created_by_name}</Td>
                  <Td cls="font-mono text-[11px] text-gray-500 whitespace-nowrap">{r.created_at?.slice(0, 10)}</Td>
                  <Td cls="font-mono text-[11px] text-gray-500 whitespace-nowrap">{r.verified_at ? r.verified_at.slice(0, 10) : "-"}</Td>
                  <Td cls={`text-right font-mono ${r.stale ? "text-amber-700 font-semibold" : ""}`}>{r.age_days != null ? `${r.age_days}d` : "-"}</Td>
                  <Td cls="text-gray-500">{r.note}{r.expires_at ? <span className="block text-[10px]">{t("ci.expires")} {r.expires_at.slice(0, 10)}</span> : null}{r.status === "released" && <span className="block text-[10px] text-red-600">{t("ci.action_release")}: {r.release_reason} · {r.released_by_name}</span>}</Td>
                  <Td>
                    {r.status === "active" && (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => keep(r)} data-testid={`ci-keep-${r.id}`}><ShieldCheck size={12} className="mr-1" />{t("ci.keep")}</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700" onClick={() => release(r)} data-testid={`ci-release-${r.id}`}><Trash2 size={12} className="mr-1" />{t("ci.release")}</Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <LogsDialog open={logsOpen} onClose={() => setLogsOpen(false)} t={t} />
      </div>
    </AppLayout>
  );
}
